import { useState } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Link,
  useTheme,
  Alert,
  CircularProgress
} from "@mui/material";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../services/api.js';
import { useNavigate } from 'react-router-dom';

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const theme = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!email) {
      setMessage({ type: 'error', text: 'Por favor, informe seu e-mail.' });
      return;
    }

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ type: 'error', text: 'Por favor, informe um e-mail válido.' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' }); // Limpar mensagem anterior

      const response = await api.post('/auth/forgot-password', {
        st_email: email.trim().toLowerCase()
      });

      console.log('Resposta do servidor:', response.data);

      if (response.data && response.data.status === 'sucesso') {
        const mensagemSucesso = response.data.mensagem || 'Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha em até 15 minutos.';
        console.log('✅ Definindo mensagem de sucesso:', mensagemSucesso);
        setMessage({
          type: 'success',
          text: mensagemSucesso
        });
        setEmail('');
      } else {
        const mensagemErro = response.data?.mensagem || 'Erro ao processar solicitação.';
        console.log('❌ Definindo mensagem de erro:', mensagemErro);
        setMessage({
          type: 'error',
          text: mensagemErro
        });
      }
    } catch (error) {
      console.error('Erro ao solicitar reset de senha:', error);
      console.error('Detalhes do erro:', {
        response: error.response?.data,
        status: error.response?.status,
        message: error.message,
        error: error
      });

      // Verificar se é erro de resposta do servidor
      if (error.response && error.response.data) {
        // Verificar se a resposta tem status de sucesso mesmo vindo como erro
        const responseData = error.response.data;
        if (responseData.status === 'sucesso') {
          setMessage({
            type: 'success',
            text: responseData.mensagem || 'Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha em até 15 minutos.'
          });
          setEmail('');
        } else {
          setMessage({
            type: 'error',
            text: responseData.mensagem || responseData.error || 'Erro ao processar solicitação. Tente novamente mais tarde.'
          });
        }
      } else if (error.status === 'sucesso') {
        // Caso o erro tenha status de sucesso diretamente
        setMessage({
          type: 'success',
          text: error.mensagem || 'Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha em até 15 minutos.'
        });
        setEmail('');
      } else {
        setMessage({
          type: 'error',
          text: error.message || 'Erro ao processar solicitação. Tente novamente mais tarde.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        padding: 2,
        position: 'relative'
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: 400,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom>
          Esqueceu sua senha?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Informe seu e-mail cadastrado e enviaremos um link para redefinir sua senha.
        </Typography>

        {message && message.text && (
          <Alert
            severity={message.type === 'success' ? 'success' : 'error'}
            sx={{ width: '100%', mb: 2 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            fullWidth
            id="email"
            label="E-mail"
            name="email"
            autoComplete="email"
            autoFocus
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || message.type === 'success'}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading || message.type === 'success'}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Enviando...' : 'Enviar Link de Recuperação'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
            >
              <ArrowBackIcon fontSize="small" />
              Voltar para o login
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* Rodapé */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        textAlign: 'center',
        padding: '12px',
        fontSize: '0.875rem',
        zIndex: 2
      }}>
        <Typography variant="body2">
          Desenvolvido por SAOTech Software. Tel. (11) 9 2039-2120 - suporte@saoinformatica.com.br
        </Typography>
      </Box>
    </Box>
  );
}

